/**
 * Offboard Asset form — opens when the "Offboard..." row action is clicked
 * on the Assets report. Lets the operator pick the outcome (Archived /
 * Disposed), supply an optional Reason + Additional Information for audit,
 * and choose whether to notify the asset's IoT provider via the supplier's
 * offboard email template.
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';

type Outcome = 'Archived' | 'Disposed';

interface AssetRow {
  id: string;
  fleetNumber?: string | null;
  registrationNumber?: string | null;
  make?: string | null;
  model?: string | null;
  vehicleStatus?: string | null;
  trackingProvider?: string | null;
}

/** One labelled detail. Always renders, so a blank field reads as
 *  "not recorded on this asset" rather than silently disappearing —
 *  a missing IoT provider changes what the operator should do next. */
function Detail({ label, value }: { label: string; value?: string | null }) {
  const shown = value && String(value).trim() ? String(value).trim() : null;
  return (
    <>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm ${shown ? 'font-medium' : 'text-muted-foreground'}`}>
        {shown ?? '—'}
      </dd>
    </>
  );
}

/** A Yes/No question that starts unanswered. Re-clicking the chosen answer
 *  clears it, so a mis-click is recoverable without a third "unset" button. */
function YesNo({
  label, value, onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-1.5 shrink-0">
        {([true, false] as const).map((v) => (
          <Button
            key={String(v)}
            type="button"
            size="sm"
            variant={value === v ? 'default' : 'outline'}
            aria-pressed={value === v}
            onClick={() => onChange(value === v ? null : v)}
            className="w-16"
          >
            {v ? 'Yes' : 'No'}
          </Button>
        ))}
      </div>
    </div>
  );
}

interface OffboardResult {
  asset: any;
  email: {
    sent: boolean;
    reason?: string;
    recipients?: string;
    subject?: string;
  };
}

export function AssetOffboardDialog({
  row, onClose, onComplete,
}: {
  row: AssetRow;
  onClose: () => void;
  onComplete?: () => void;
}) {
  const [outcome, setOutcome] = React.useState<Outcome>('Archived');
  const [reason, setReason] = React.useState('');
  const [additionalInfo, setAdditionalInfo] = React.useState('');
  const [gpsUnitRemoved, setGpsUnitRemoved] = React.useState<boolean | null>(null);
  const [fuelTagRemoved, setFuelTagRemoved] = React.useState<boolean | null>(null);

  // Re-read the asset by id rather than trusting `row`. The two entry points
  // hand over different shapes — the Assets report passes whatever columns
  // that report happens to select, the picker passes its own list row — so
  // relying on the caller would leave fields blank depending on where the
  // operator came from. `row` seeds the display so nothing flickers.
  const { data: detail } = useQuery({
    queryKey: ['/fleet/assets', row.id, 'offboard-summary'],
    queryFn: async () => (await api<AssetRow>(`/fleet/assets/${row.id}`)).data ?? null,
  });
  const asset: AssetRow = { ...row, ...(detail ?? {}) };

  // Default the IoT-notify toggle to ON when the asset has a tracking
  // provider configured (otherwise the offboard email is a no-op anyway).
  const hasIot = !!(asset.trackingProvider && asset.trackingProvider.trim());
  const [notifyIot, setNotifyIot] = React.useState(!!(row.trackingProvider && row.trackingProvider.trim()));

  // The provider may only become known once the fetch lands; adopt it then,
  // but never override a choice the operator has already made.
  const touchedNotify = React.useRef(false);
  React.useEffect(() => {
    if (!touchedNotify.current) setNotifyIot(hasIot);
  }, [hasIot]);

  const submit = useMutation({
    mutationFn: async () => {
      const r = await api<OffboardResult>(`/fleet/assets/${row.id}/offboard`, {
        method: 'POST',
        body: JSON.stringify({
          outcome,
          reason: reason.trim() || null,
          additionalInfo: additionalInfo.trim() || null,
          notifyIot,
          gpsUnitRemoved,
          fuelTagRemoved,
        }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Offboard failed');
      return r.data!;
    },
    onSuccess: (data) => {
      // Two toasts: first the status change, then the email outcome —
      // operators want to see at a glance whether the IoT provider was
      // actually notified or if the send was skipped.
      toast(
        `Asset ${outcome.toLowerCase()}`,
        `${asset.fleetNumber ?? row.id} moved to ${outcome}`,
        'success',
      );
      if (data.email?.sent) {
        toast(
          'IoT provider notified',
          `Email sent to ${data.email.recipients ?? '(recipients)'}`,
          'success',
        );
      } else if (data.email?.reason) {
        toast(
          'IoT email not sent',
          data.email.reason,
          'default',
        );
      }
      onComplete?.();
    },
    onError: (e: any) => toast('Error', e.message ?? String(e), 'error'),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Offboard Asset</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Fleet details for the selected asset — shown so the operator can
              confirm they are offboarding the right vehicle before committing
              to a status change that also emails the IoT provider. */}
          <div className="rounded-md border p-3 bg-muted/30 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Fleet details
              </span>
              {asset.vehicleStatus && (
                <Badge variant="secondary" className="ml-auto">{asset.vehicleStatus}</Badge>
              )}
            </div>
            <dl className="grid grid-cols-[7.5rem_1fr] sm:grid-cols-[7.5rem_1fr_7.5rem_1fr] gap-x-3 gap-y-1.5 items-baseline">
              <Detail label="Fleet #" value={asset.fleetNumber} />
              <Detail label="Registration" value={asset.registrationNumber} />
              <Detail label="Make" value={asset.make} />
              <Detail label="Model" value={asset.model} />
              <Detail label="IoT provider" value={asset.trackingProvider} />
            </dl>
          </div>

          {/* Removal checklist — asked before the outcome because these are
              physical facts about the vehicle, and the answers are what the
              IoT provider and fuel-card admin need to act on. Left unanswered
              they save as null, not "No". */}
          <div className="space-y-2">
            <YesNo
              label="Have GPS unit been removed"
              value={gpsUnitRemoved}
              onChange={setGpsUnitRemoved}
            />
            <YesNo
              label="Has Fuel Tag been removed"
              value={fuelTagRemoved}
              onChange={setFuelTagRemoved}
            />
          </div>

          {/* Outcome selector */}
          <div className="space-y-2">
            <Label className="text-sm">Outcome <span className="text-destructive">*</span></Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setOutcome('Archived')}
                className={`rounded-md border p-3 text-left transition ${
                  outcome === 'Archived' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:border-primary/40'
                }`}
              >
                <div className="font-medium">Archive</div>
                <div className="text-xs text-muted-foreground">
                  Move out of the active fleet. Reversible — can be re-activated later.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setOutcome('Disposed')}
                className={`rounded-md border p-3 text-left transition ${
                  outcome === 'Disposed' ? 'border-destructive ring-1 ring-destructive bg-destructive/5' : 'hover:border-destructive/40'
                }`}
              >
                <div className="font-medium">Dispose</div>
                <div className="text-xs text-muted-foreground">
                  Terminal — sold / written off / scrapped.
                </div>
              </button>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label htmlFor="offboard-reason" className="text-sm">Reason <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input
              id="offboard-reason"
              placeholder="e.g. End of lease, written off after accident"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Additional Information */}
          <div className="space-y-1.5">
            <Label htmlFor="offboard-additional" className="text-sm">
              Additional Information <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="offboard-additional"
              placeholder="Notes for the audit log or IoT provider — install location, removal date, etc."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              rows={3}
            />
          </div>

          {/* IoT notify toggle */}
          <div className="flex items-start gap-2.5 rounded-md border p-3">
            <input
              id="offboard-notify"
              type="checkbox"
              checked={notifyIot}
              disabled={!hasIot}
              onChange={(e) => { touchedNotify.current = true; setNotifyIot(e.target.checked); }}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <div className="flex-1">
              <Label htmlFor="offboard-notify" className={`text-sm font-medium ${!hasIot ? 'text-muted-foreground' : ''}`}>
                <Send className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                Notify IoT provider
              </Label>
              <div className="text-xs text-muted-foreground mt-0.5">
                {hasIot
                  ? <>Sends the offboard email template configured for <span className="font-medium text-foreground">{asset.trackingProvider}</span>.</>
                  : <>This asset has no IoT provider configured — no email will be sent.</>}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            variant={outcome === 'Disposed' ? 'destructive' : 'default'}
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
          >
            {submit.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {outcome === 'Disposed' ? 'Dispose Asset' : 'Archive Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
